import { variantLabel } from "src/cep/messages/components/message-builder/ab-tests/variant-label";
import { NODE_WIDTH } from "src/pages/journeys/constants";
import type { JourneyMessageConfig } from "src/pages/journeys/types";

// DB-canonical elements, matching MessageAbTestElement.
export const BODY_ELEMENT = "body";
export const SUBJECT_ELEMENT = "subject_preheader";

const KNOWN_ELEMENTS = new Set<string>([BODY_ELEMENT, SUBJECT_ELEMENT]);

export type NodeExperiment = NonNullable<
  JourneyMessageConfig["experiments"]
>[number];
export type NodeExperimentArm = NodeExperiment["arms"][number];

export type NodeAbTestEntry = {
  element: string;
  splits: number;
  measuring: string;
};

// For a test with no stored goal: older than the column, or created when
// auto-config had no managed model to point at.
const measuringForElement = (element: string): string =>
  element === SUBJECT_ELEMENT ? "opens" : "clicks";

// The chosen metric names itself; without it the element implies the default.
function measuringLabel(experiment: NodeExperiment): string {
  const goal = experiment.primary_metric?.goal_definition;
  const name =
    goal != null && typeof goal === "object" && !Array.isArray(goal)
      ? (goal as { name?: unknown }).name
      : undefined;
  return typeof name === "string" && name.length > 0
    ? name
    : measuringForElement(experiment.element);
}

// A test only counts once it has 2+ arms — a single-arm draft shows nothing.
const hasSplits = (experiment: NodeExperiment): boolean =>
  experiment.arms.length >= 2;

// Labelling is the caller's job: this module stays free of `src/cep/messages/constants`,
// which drags the GraphQL layer in behind it and into every importer of `layout.ts`.
// Tooltip rows for every real A/B test on the message (subject and/or content).
export function nodeAbTestEntries(
  experiments: NodeExperiment[] | null | undefined,
): NodeAbTestEntry[] {
  return (experiments ?? [])
    .filter((e) => KNOWN_ELEMENTS.has(e.element))
    .filter(hasSplits)
    .map((experiment) => ({
      element: experiment.element,
      splits: experiment.arms.length,
      measuring: measuringLabel(experiment),
    }));
}

// The content (body) test — the only element whose variants differ in body
// content and so drive the per-variant tile previews.
export function bodyAbTest(
  experiments: NodeExperiment[] | null | undefined,
): NodeExperiment | null {
  const body = (experiments ?? []).find((e) => e.element === BODY_ELEMENT);
  return body && hasSplits(body) ? body : null;
}

export function bodyAbTestArms(
  experiments: NodeExperiment[] | null | undefined,
): NodeExperimentArm[] {
  return bodyAbTest(experiments)?.arms ?? [];
}

export function subjectAbTest(
  experiments: NodeExperiment[] | null | undefined,
): NodeExperiment | null {
  const subject = (experiments ?? []).find(
    (e) => e.element === SUBJECT_ELEMENT,
  );
  return subject && hasSplits(subject) ? subject : null;
}

export function subjectAbTestArms(
  experiments: NodeExperiment[] | null | undefined,
): NodeExperimentArm[] {
  return subjectAbTest(experiments)?.arms ?? [];
}

// The backend's checkFieldCoverage rule: under a subject test the arms are
// what send, so every arm needs its own subject — a filled base row does NOT
// excuse a blank arm (its recipients would get base copy while still being
// counted under the arm, so dispatch fails the whole fragment instead).
// Returns the labels of the arms that fail, for the validation message.
export function uncoveredSubjectArms(
  experiments: NodeExperiment[] | null | undefined,
): string[] {
  return subjectAbTestArms(experiments)
    .filter((arm) => !arm.subject?.trim())
    .map((arm) =>
      variantLabel({ variantKey: arm.variant_key, name: arm.name }),
    );
}

// The node never widens for A/B: this many arm previews fit the fixed node
// width, and any beyond that overflow a clipped row and fade out (see
// NodeAbTestPreview) rather than growing or scrolling the node.
export const MAX_VISIBLE_AB_VARIANTS = 3;
/** Up to this many arms sit side by side with a gap; past it they overlap. */
export const MAX_SPACED_AB_VARIANTS = 2;
const TILE_GAP = 8; // gap between the two side-by-side tiles
const CONTENT_X_PADDING = 32; // the tile body's p={4}, both sides

/**
 * Width to lay the row out at before it has been measured. Only the padding is
 * knowable up front: the tile is `box-sizing: border-box` and its border is 1-3px
 * depending on node state (`tile-style-utils`), so the real usable width is up to
 * 6px narrower. NodeAbTestPreview measures the row and passes the true width;
 * this is the one-frame fallback.
 */
export const NOMINAL_AB_VARIANT_ROW_WIDTH = NODE_WIDTH - CONTENT_X_PADDING;

// Measured off the Figma (cards spanning a 336px row): at three and at four
// arms the card is the same 128px, and the extra arm is absorbed by overlapping
// its left neighbour rather than by shrinking every card. Holding the width is
// what keeps the message legible as arms are added.
const OVERLAPPED_TILE_WIDTH_RATIO = 128 / 336;
// The overlap the design tops out at, from the four-arm example (32px of 128).
// The three-arm example sits below it at 24px, because three spread out to fill
// the row exactly. Past four the row overflows and the last card fades out
// instead of the cards compressing further and turning the text to mush.
const MAX_TILE_OVERLAP_RATIO = 32 / 128;

export type AbVariantTileLayout = {
  /** Rendered width of one arm's tile. */
  width: number;
  /**
   * Distance from one tile's left edge to the next. Smaller than `width` when
   * the tiles overlap, larger by `TILE_GAP` when they sit apart.
   */
  pitch: number;
};

/**
 * Per-arm tile geometry for the node preview, laid out against the row's
 * measured width. Two arms keep the side-by-side gap; three or more hold a
 * constant width and overlap, so the node never widens and the copy never
 * shrinks with the arm count.
 */
export const getAbVariantTileLayout = (
  armCount: number,
  rowWidth: number,
): AbVariantTileLayout => {
  if (armCount <= MAX_SPACED_AB_VARIANTS) {
    const visible = Math.max(armCount, 1);
    const width = (rowWidth - (visible - 1) * TILE_GAP) / visible;
    return { width, pitch: width + TILE_GAP };
  }
  const width = rowWidth * OVERLAPPED_TILE_WIDTH_RATIO;
  const minPitch = width * (1 - MAX_TILE_OVERLAP_RATIO);
  // Spread the arms over the row while they still fit; once they don't, hold
  // the maximum overlap and let the last card run off the right edge.
  const pitch = Math.max(minPitch, (rowWidth - width) / (armCount - 1));
  return { width, pitch };
};
